/**
 * GEO — Generative Engine Optimization
 * 生成式引擎优化模块
 *
 * Row gutter={24} 左右布局:
 *  - 左Col(8): 产品选择 + AI搜索引擎Checkbox + 生成Button
 *  - 右Col(16): 生成结果Card + 预估AI引用覆盖率Progress + 复制Button
 */

import { useState, useEffect } from 'react';
import {
  Row, Col, Select, Checkbox, Button, Card, Progress, Typography,
  Tag, Space, message, Spin, Divider,
} from 'antd';
import {
  GlobalOutlined, RobotOutlined, CopyOutlined, CheckCircleOutlined,
  ThunderboltOutlined, TrophyOutlined, BulbOutlined,
} from '@ant-design/icons';
import { geoAPI } from '../../api/geo';

const { Title, Text, Paragraph } = Typography;

// ─── 颜色常量 ───
const C = {
  cardBg: 'rgba(255,255,255,0.05)',
  border: 'rgba(255,255,255,0.08)',
  accent: '#6366f1',
  accentSoft: 'rgba(99,102,241,0.15)',
  gold: '#E8C85A',
  text: 'rgba(240,245,250,0.95)',
  textSec: 'rgba(240,245,250,0.6)',
  textMute: 'rgba(240,245,250,0.35)',
  green: '#22c55e',
  cyan: '#06b6d4',
};

// 平台图标映射
const PLATFORM_ICONS = {
  'ChatGPT Search': '🤖',
  'Google AI Overview': '🔍',
  'Perplexity': '🌐',
  'Claude Search': '🧠',
};

const GEO = () => {
  const [products, setProducts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(undefined);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  // 初始化
  useEffect(() => {
    Promise.all([geoAPI.getProducts(), geoAPI.getPlatforms()]).then(([p, pl]) => {
      setProducts(p);
      setPlatforms(pl);
      setLoading(false);
    });
  }, []);

  // 生成
  const handleGenerate = async () => {
    if (!selectedProduct) { message.warning('请选择产品'); return; }
    if (!selectedPlatforms.length) { message.warning('请至少选择一个AI搜索引擎'); return; }
    setGenerating(true);
    setResult(null);
    try {
      const data = await geoAPI.generate(selectedProduct, selectedPlatforms);
      setResult(data);
      message.success('GEO内容生成成功！');
    } catch (err) {
      message.error(err.message || '生成失败');
    } finally {
      setGenerating(false);
    }
  };

  // 复制内容
  const handleCopy = async () => {
    if (!result?.content) return;
    try {
      await geoAPI.copyContent(result.content);
      message.success('内容已复制到剪贴板');
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = result.content;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      message.success('内容已复制到剪贴板');
    }
  };

  // 获取覆盖率颜色
  const getCoverageColor = (val) => {
    if (val >= 80) return C.green;
    if (val >= 60) return C.gold;
    if (val >= 40) return C.cyan;
    return C.accent;
  };

  return (
    <Spin spinning={loading}>
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 32px' }}>
        {/* 页面标题 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={3} style={{ color: C.text, margin: 0 }}>
            <GlobalOutlined style={{ color: C.cyan, marginRight: 10 }} />
            GEO 生成式引擎优化
          </Title>
          <Text style={{ color: C.textSec, fontSize: 14 }}>
            为AI搜索引擎生成优化内容，提升品牌AI引用覆盖率
          </Text>
        </div>

        {/* 主布局: 左右分栏 */}
        <Row gutter={24}>
          {/* ─── 左Col(8): 配置区 ─── */}
          <Col span={8}>
            <Card
              title={
                <span style={{ color: C.text }}>
                  <BulbOutlined style={{ marginRight: 8 }} />
                  内容生成配置
                </span>
              }
              style={{
                background: C.cardBg,
                border: `1px solid ${C.border}`,
                borderRadius: 14,
                position: 'sticky',
                top: 84,
              }}
            >
              {/* 产品选择 */}
              <div style={{ marginBottom: 24 }}>
                <Text style={{ color: C.textSec, fontSize: 13, marginBottom: 8, display: 'block', fontWeight: 500 }}>
                  选择产品 / 服务
                </Text>
                <Select
                  placeholder="请选择要优化的产品"
                  style={{ width: '100%' }}
                  size="large"
                  value={selectedProduct}
                  onChange={setSelectedProduct}
                  options={products.map((p) => ({ value: p, label: p }))}
                />
              </div>

              {/* AI搜索引擎选择 */}
              <div style={{ marginBottom: 28 }}>
                <Text style={{ color: C.textSec, fontSize: 13, marginBottom: 12, display: 'block', fontWeight: 500 }}>
                  目标AI搜索引擎
                </Text>
                <Checkbox.Group
                  value={selectedPlatforms}
                  onChange={setSelectedPlatforms}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {platforms.map((p) => (
                      <div
                        key={p}
                        style={{
                          padding: '10px 14px',
                          background: selectedPlatforms.includes(p) ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.02)',
                          border: `1px solid ${selectedPlatforms.includes(p) ? 'rgba(99,102,241,0.3)' : C.border}`,
                          borderRadius: 10,
                          transition: 'all 200ms',
                        }}
                      >
                        <Checkbox value={p} style={{ width: '100%' }}>
                          <Space>
                            <span style={{ fontSize: 18 }}>{PLATFORM_ICONS[p] || '🔎'}</span>
                            <span style={{ color: C.text, fontSize: 13 }}>{p}</span>
                          </Space>
                        </Checkbox>
                      </div>
                    ))}
                  </Space>
                </Checkbox.Group>
              </div>

              {/* 全选/取消 */}
              <div style={{ marginBottom: 24 }}>
                <Space size={8}>
                  <Button
                    size="small"
                    type="link"
                    style={{ color: C.accent, padding: 0 }}
                    onClick={() => setSelectedPlatforms(platforms)}
                  >
                    全选
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    style={{ color: C.textMute, padding: 0 }}
                    onClick={() => setSelectedPlatforms([])}
                  >
                    清空
                  </Button>
                </Space>
              </div>

              {/* 生成Button */}
              <Button
                type="primary"
                block
                size="large"
                icon={generating ? <RobotOutlined spin /> : <ThunderboltOutlined />}
                onClick={handleGenerate}
                loading={generating}
                style={{
                  height: 48,
                  fontSize: 15,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  border: 'none',
                  borderRadius: 12,
                }}
              >
                {generating ? '生成中...' : '生成GEO优化内容'}
              </Button>

              {/* 提示 */}
              <div style={{ marginTop: 16, padding: 12, background: 'rgba(232,200,90,0.05)', borderRadius: 10, border: '1px solid rgba(232,200,90,0.12)' }}>
                <Text style={{ color: C.gold, fontSize: 11, lineHeight: 1.6 }}>
                  <TrophyOutlined style={{ marginRight: 4 }} />
                  建议同时选择多个AI搜索引擎以获得最佳覆盖率
                </Text>
              </div>
            </Card>
          </Col>

          {/* ─── 右Col(16): 结果区 ─── */}
          <Col span={16}>
            {!result && !generating && (
              <Card
                style={{
                  background: C.cardBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 14,
                  textAlign: 'center',
                  padding: '80px 20px',
                }}
              >
                <GlobalOutlined style={{ fontSize: 56, color: C.textMute, marginBottom: 20, display: 'block' }} />
                <Text style={{ color: C.textSec, fontSize: 16, display: 'block', marginBottom: 8 }}>
                  配置左侧参数后点击生成按钮
                </Text>
                <Text style={{ color: C.textMute, fontSize: 13 }}>
                  系统将自动生成针对各AI搜索引擎的优化内容
                </Text>
              </Card>
            )}

            {generating && (
              <Card
                style={{
                  background: C.cardBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 14,
                  textAlign: 'center',
                  padding: '80px 20px',
                }}
              >
                <Spin size="large" />
                <div style={{ marginTop: 20 }}>
                  <Text style={{ color: C.textSec, fontSize: 15 }}>
                    AI正在分析产品并生成优化内容...
                  </Text>
                  <div style={{ marginTop: 12, maxWidth: 300, margin: '12px auto 0' }}>
                    <Progress percent={Math.min(90, Math.floor(Math.random() * 80) + 10)} strokeColor={C.accent} showInfo={false} />
                  </div>
                </div>
              </Card>
            )}

            {result && !generating && (
              <>
                {/* 生成结果 Card */}
                <Card
                  title={
                    <span style={{ color: C.text }}>
                      <RobotOutlined style={{ marginRight: 8 }} />
                      {result.title}
                    </span>
                  }
                  style={{
                    background: C.cardBg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 14,
                    marginBottom: 20,
                  }}
                  extra={
                    <Space>
                      {result.platforms.map((p) => (
                        <Tag key={p} color="blue" style={{ fontSize: 11 }}>
                          {PLATFORM_ICONS[p]} {p}
                        </Tag>
                      ))}
                    </Space>
                  }
                >
                  {/* Markdown内容预览 */}
                  <div
                    style={{
                      background: 'rgba(0,0,0,0.25)',
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      padding: '20px 24px',
                      maxHeight: 520,
                      overflow: 'auto',
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      fontSize: 13,
                      lineHeight: 1.8,
                      color: C.textSec,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {result.content}
                  </div>

                  <Divider style={{ borderColor: C.border, margin: '16px 0 0 0' }} />

                  {/* 操作栏 */}
                  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: C.textMute, fontSize: 12 }}>
                      生成时间: {new Date(result.generatedAt).toLocaleString('zh-CN')}
                    </Text>
                    <Button
                      type="primary"
                      icon={<CopyOutlined />}
                      onClick={handleCopy}
                    >
                      复制全部内容
                    </Button>
                  </div>
                </Card>

                {/* 预估AI引用覆盖率 */}
                <Card
                  title={
                    <span style={{ color: C.text }}>
                      <TrophyOutlined style={{ marginRight: 8, color: C.gold }} />
                      预估AI引用覆盖率
                    </span>
                  }
                  style={{
                    background: C.cardBg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 14,
                  }}
                >
                  <div style={{ padding: '8px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <Text style={{ color: C.textSec }}>综合覆盖率</Text>
                      <Text style={{ color: getCoverageColor(result.coverage), fontSize: 24, fontWeight: 700 }}>
                        {result.coverage}%
                      </Text>
                    </div>
                    <Progress
                      percent={result.coverage}
                      strokeColor={{
                        '0%': C.accent,
                        '100%': getCoverageColor(result.coverage),
                      }}
                      trailColor="rgba(255,255,255,0.06)"
                      size="large"
                      style={{ marginBottom: 20 }}
                    />

                    {/* 各平台详情 */}
                    <div>
                      <Text style={{ color: C.textSec, fontSize: 13, marginBottom: 12, display: 'block' }}>
                        各平台预估覆盖率
                      </Text>
                      <Space direction="vertical" style={{ width: '100%' }} size={10}>
                        {result.platforms.map((p, i) => {
                          const platformCoverage = Math.min(95, result.coverage - 5 + i * 4 + Math.floor(Math.random() * 6));
                          return (
                            <div key={p}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <Text style={{ color: C.text, fontSize: 12 }}>
                                  {PLATFORM_ICONS[p]} {p}
                                </Text>
                                <Text style={{ color: getCoverageColor(platformCoverage), fontSize: 12, fontWeight: 600 }}>
                                  {platformCoverage}%
                                </Text>
                              </div>
                              <Progress
                                percent={platformCoverage}
                                strokeColor={{
                                  '0%': C.accent,
                                  '100%': getCoverageColor(platformCoverage),
                                }}
                                trailColor="rgba(255,255,255,0.06)"
                                showInfo={false}
                                size="small"
                              />
                            </div>
                          );
                        })}
                      </Space>
                    </div>

                    <Divider style={{ borderColor: C.border }} />

                    {/* 底部总结 */}
                    <div style={{
                      padding: 16,
                      background: 'rgba(34,197,94,0.06)',
                      borderRadius: 10,
                      border: '1px solid rgba(34,197,94,0.15)',
                    }}>
                      <Space>
                        <CheckCircleOutlined style={{ color: C.green, fontSize: 18 }} />
                        <Text style={{ color: C.green, fontSize: 13 }}>
                          预计您的品牌在AI搜索结果中的引用率将提升 <strong>{result.coverage}%</strong>，
                          覆盖 {result.platforms.length} 个AI搜索引擎
                        </Text>
                      </Space>
                    </div>
                  </div>
                </Card>
              </>
            )}
          </Col>
        </Row>
      </div>
    </Spin>
  );
};

export default GEO;
